import type {
  InventorySnapshotVolume,
  Snapshot,
  InventoryResource,
} from '@repo/api-gw-sdk';

import { useViewParameters } from '@/contexts/navigation';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { SidePanelStore } from '@/contexts/workspaceContext';

import { SnapshotDetails } from './snapshotDetails';
import type { RestoreActionType, VMSnapshotRowProps } from './vmSnapshotRow';

import { Panels } from '../../../panels';

export const AwsEc2SnapshotRow = ({
  snap,
  vault,
  isSelected,
  onSelect,
  resource,
  snapshotSelection,
  mutateSnapshots,
}: VMSnapshotRowProps) => {
  const { rightPanel } = useWorkspace();
  const { removeParam } = useViewParameters();

  if (!snap.resource?.properties?.awsEc2?.volumes) {
    return null;
  }

  const onClose = () => {
    rightPanel.setIsOpen(false);
    removeParam('id');
  };

  const onBack = () => {
    rightPanel.setComponent({
      panel: Panels.InventoryInstance,
      props: {
        id: resource.id,
        onClose,
        initialTab: 'eon-snapshots',
      },
    });
  };

  return (
    <SnapshotDetails
      testid='vm-snapshot-row'
      resource={resource}
      isSelected={isSelected}
      onSelect={onSelect}
      snap={snap}
      vault={vault}
      snapshotSelection={snapshotSelection}
      mutateSnapshots={mutateSnapshots}
      onRestore={(volumes, actionType) =>
        onAWSRestore(
          volumes.filter((x) => 'availabilityZone' in x),
          actionType,
          rightPanel,
          snap,
          resource,
          onClose,
          onBack
        )
      }
      onConvertSnapshot={(volumes, actionType) =>
        onAWSRestore(
          volumes.filter((x) => 'availabilityZone' in x),
          actionType,
          rightPanel,
          snap,
          resource,
          onClose,
          onBack
        )
      }
    />
  );
};

const onAWSRestore = (
  vols: InventorySnapshotVolume[],
  actionType: RestoreActionType,
  panel: SidePanelStore,
  snapshot: Snapshot,
  entity: InventoryResource,
  onClose: () => void,
  onBack: () => void
) => {
  if (vols.length === 0) {
    return;
  }

  switch (actionType) {
    case 'full-instance':
      panel.setComponent({
        panel: Panels.RestoreEc2InstanceWizard,
        props: {
          snapshot,
          resource: entity,
          resourceName: entity.resourceName,
          volumes: vols,
          onClose,
          onBack,
        },
      });
      break;
    case 'convert-image':
      panel.setComponent({
        panel: Panels.ConvertToAMIWizard,
        props: {
          snapshot,
          resource: entity,
          resourceId: entity.id,
          resourceName: entity.resourceName,
          volumes: vols,
          onClose,
          onBack,
        },
      });
      break;
    case 'volume':
    case 'convert-snapshot':
      panel.setComponent({
        panel: Panels.RestoreVolumeWizard,
        props: {
          snapshot,
          resource: entity,
          providerVolumeId: vols[0].providerVolumeId,
          actionType,
          onClose,
          onBack,
        },
      });
  }
};
